// Enhanced text preprocessing with citation support
import PropTypes from "prop-types";
export const deepSearchPreprocessText = (text, annotations = []) => {
  if (!text) return "";


  let processedText = text
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "    ")
    .replace(/\\\\/g, "\\")
    .replace(/^(#+ .+)$/gm, "$1\n")
    .replace(/(<ul>.*?<\/ul>)/gs, "$1\n")
    .trim();

  // Process footnote citations [^1], [^2], etc.
  if (annotations && annotations.length > 0) {
    annotations.forEach((annotation, index) => {
      if (annotation.type === "url_citation") {
        const footnotePattern = new RegExp(`\\[\\^${index + 1}\\]`, "g");
        const citationId = `citation-${index + 1}`;
        processedText = processedText.replace(
          footnotePattern,
          `<sup class="citation-link" data-citation-id="${citationId}" data-url="${annotation.url_citation.url
          }" data-title="${annotation.url_citation.title}" data-date="${annotation.url_citation.dateTime
          }">[${index + 1}]</sup>`
        );
      }
    });
  }

  return processedText;
};

export const deepSearchCustomLink = ({ children, ...props }) => {
  return (
    <a
      {...props}
      className="text-[#0645AD] hover:text-[#0B0080] hover:underline transition-colors"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
};

export const CitationLink = ({
  children,
  citationId,
  url,
  title,
  date,
  onClick,
}) => {
  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) {
      onClick({ citationId, url, title, date });
    }
  };

  return (
    <button
      onClick={handleClick}
      title={`${title} - ${date}`}
      aria-label={`Citation: ${title}, ${date}`}
      className="citation-link align-super text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-1 py-0.5 rounded text-xs font-medium transition-colors cursor-pointer border-none bg-transparent p-0 m-0"
      type="button"
    >
      {children}
    </button>
  );
};

// Citation modal/tooltip component
export const CitationTooltip = ({ citation, isVisible, onClose }) => {
  if (!isVisible || !citation) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-96 overflow-y-auto">
        <div className="p-4 border-b">
          <div className="flex justify-between items-start">
            <h3 className="font-semibold text-lg pr-4">{citation.title}</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl leading-none"
            >
              ×
            </button>
          </div>
        </div>
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-2">{citation.date}</p>
          {citation.exactQuote && (
            <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-700 mb-3">
              "{citation.exactQuote}"
            </blockquote>
          )}
          <a
            href={citation.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline text-sm break-all"
          >
            {citation.url}
          </a>
        </div>
        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={() => window.open(citation.url, "_blank")}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
          >
            Visit Source
          </button>
        </div>
      </div>
    </div>
  );
};

// Sources section component
export const SourcesSection = ({ annotations, visitedURLs }) => {
  if (!annotations || annotations.length === 0) return null;

  return (
    <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
      <h4 className="font-semibold mb-3 text-gray-800">Sources</h4>
      <div className="space-y-2">
        {annotations.map((annotation, index) => {
          if (annotation.type === "url_citation") {
            const { title, url, dateTime } = annotation.url_citation;
            return (
              <div key={`${url}-${index}`} className="text-sm">
                <span className="font-medium text-blue-600">[{index + 1}]</span>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {title}
                </a>
                {dateTime && (
                  <span className="ml-2 text-gray-500">- {dateTime}</span>
                )}
              </div>
            );
          }
          return null;
        })}
      </div>

      {visitedURLs && visitedURLs.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800">
            Additional URLs Visited ({visitedURLs.length})
          </summary>
          <div className="mt-2 max-h-32 overflow-y-auto">
            {visitedURLs.slice(0, 10).map((url, index) => (
              <div key={`${url}-${index}`} className="text-xs text-gray-500 truncate">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-blue-600"
                >
                  {url}
                </a>
              </div>
            ))}
            {visitedURLs.length > 10 && (
              <div className="text-xs text-gray-400 mt-1">
                +{visitedURLs.length - 10} more URLs
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
};


CitationLink.propTypes = {
  children: PropTypes.node,
  citationId: PropTypes.string.isRequired,
  url: PropTypes.string,
  title: PropTypes.string,
  date: PropTypes.string,
  onClick: PropTypes.func,
};
CitationTooltip.propTypes = {
  citation: PropTypes.object,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
};
SourcesSection.propTypes = {
  annotations: PropTypes.array.isRequired,
  visitedURLs: PropTypes.array.isRequired,
};